import { computed, inject, provide, ref, shallowReactive, type InjectionKey, type Ref, type WritableComputedRef } from 'vue'

import { LANGUAGES, Language } from '@/enums/language'
import i18n from '@/libs/i18n'
import { useTenantStore } from '@/stores/tenant'
import type { LocalizedText, SelectOption } from '@/types/Common'

/*
 * LOCALIZED TEXTS (docs/API.md §1 Common: LocalizedText). The console reads and writes the whole
 * object; the API drops keys outside Language and empty strings, so this file only has to show
 * the tenant's enabled languages and send what was typed.
 */

/** A content language's name, written in itself: "Slovenščina", "Deutsch". */
export const languageLabel = (code: string): string =>
    LANGUAGES.includes(code as Language) ? i18n.global.t(`language.${code}`) : code.toUpperCase()

export const languageOptions = (codes: string[] = LANGUAGES): SelectOption[] =>
    codes.map((code) => ({ label: languageLabel(code), value: code }))

/** The text in `lang`, else in the tenant's default language, else the first non-empty one. */
export const pickText = (text: LocalizedText | null | undefined, lang: string, fallback = ''): string => {
    if (!text) return ''

    return text[lang] || (fallback ? text[fallback] : '') || Object.values(text).find((value) => !!value) || ''
}

/** A copy without empty texts — what the API keeps anyway, sent tidy. */
export const cleanText = (text: LocalizedText | null | undefined): LocalizedText => {
    const result: LocalizedText = {}

    for (const [lang, value] of Object.entries(text ?? {})) {
        const trimmed = (value ?? '').trim()

        if (trimmed) result[lang] = trimmed
    }

    return result
}

/** Whether a text has a non-empty value in at least one language. */
export const hasText = (text: LocalizedText | null | undefined): boolean =>
    Object.values(text ?? {}).some((value) => !!value?.trim())

/** The tenant's content languages, default first — what every LocalizedText editor offers. */
export const useTenantLanguages = () => {
    const tenantStore = useTenantStore()

    const languages = computed((): string[] => {
        const tenant = tenantStore.tenant
        if (!tenant) return [Language.Sl]

        const list = tenant.languages.length > 0 ? tenant.languages : [tenant.default_language || Language.Sl]

        return [...list].sort((a, b) => Number(b === tenant.default_language) - Number(a === tenant.default_language))
    })

    const defaultLanguage = computed((): string => tenantStore.tenant?.default_language || languages.value[0] || Language.Sl)

    /** A text as a list shows it: in the default language (or whatever there is). */
    const show = (text: LocalizedText | null | undefined): string => pickText(text, defaultLanguage.value)

    return { languages, defaultLanguage, show }
}

/* ── the language being edited ─────────────────────────────────────────────── */

/**
 * How one language of a text stands: typed (`filled`), empty while another language has text —
 * the screen then shows that one (`fallback`, see pickText) — or the whole text is empty.
 */
export type TextMark = 'filled' | 'fallback' | 'empty'

export const textMark = (text: LocalizedText | null | undefined, lang: string): TextMark => {
    if (text?.[lang]?.trim()) return 'filled'

    return hasText(text) ? 'fallback' : 'empty'
}

/** The languages a text with some content still lacks (none for an empty text). */
export const missingLanguages = (text: LocalizedText | null | undefined, languages: string[]): string[] =>
    hasText(text) ? languages.filter((lang) => !text?.[lang]?.trim()) : []

/**
 * One LocalizedInput under a language switch: how the switch reads it (the missing counts), and
 * how »Prevedi manjkajoče« fills it — `write` adds languages to the form's model, `max` is the
 * field's limit per language, a `disabled` field is never filled.
 */
export interface ILocalizedField {
    read:      () => LocalizedText | null | undefined
    languages: () => string[]
    write:     (added: LocalizedText) => void
    max:       () => number
    disabled:  () => boolean
}

/** The limit a field without `maxlength` is translated within: the API's short and long texts (§5.9). */
export const SHORT_TEXT_LIMIT = 120
export const LONG_TEXT_LIMIT  = 2000

/**
 * A text with the translations added — only into languages that are still empty, so nothing
 * typed meanwhile is overwritten.
 */
export const withAddedLanguages = (text: LocalizedText | null | undefined, added: LocalizedText): LocalizedText => {
    const result: LocalizedText = { ...(text ?? {}) }

    for (const [lang, value] of Object.entries(added)) {
        if (!result[lang]?.trim() && value?.trim()) result[lang] = value
    }

    return result
}

/**
 * A page's or a dialog's LANGUAGE SWITCH ("Urejam v: SL · EN · DE"): the language every
 * LocalizedInput under it shows, and the fields that have registered, so the switch can count
 * the missing translations. A field's own chips override it until the switch moves again.
 */
export interface ILocalizedEditor {
    active:  WritableComputedRef<string>
    /** Every field under the switch in the old layout, one row per language. */
    showAll: Ref<boolean>
    fields:  Map<symbol, ILocalizedField>
    /** Back to the outer switch's language (a dialog calls it when it opens). */
    reset:   () => void
}

const LOCALIZED_EDITOR: InjectionKey<ILocalizedEditor> = Symbol('localized-editor')

/**
 * Opens a language switch for the component's subtree. A nested one (a dialog's) follows the
 * outer one until a language is chosen in it, and reset() follows it again.
 */
export const provideLocalizedEditor = (): ILocalizedEditor => {
    const outer = inject(LOCALIZED_EDITOR, null)
    const { languages, defaultLanguage } = useTenantLanguages()

    // null = follow: the outer switch's language, else the tenant's default (also one that loads later).
    const chosen    = ref<string | null>(null)
    const following = (): string => outer?.active.value ?? defaultLanguage.value

    const active = computed({
        get: (): string => (chosen.value && languages.value.includes(chosen.value) ? chosen.value : following()),
        set: (lang: string) => { chosen.value = lang === following() ? null : lang },
    })

    const editor: ILocalizedEditor = {
        active,
        showAll: ref(false),
        fields:  shallowReactive(new Map<symbol, ILocalizedField>()),
        reset:   () => { chosen.value = null },
    }

    provide(LOCALIZED_EDITOR, editor)

    return editor
}

/** The nearest language switch, or null where a field stands on its own. */
export const useLocalizedEditor = (): ILocalizedEditor | null => inject(LOCALIZED_EDITOR, null)
